import React, { useMemo, useState } from 'react';
import { Input, Select, Spin, Tooltip } from 'antd';
import { CheckOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import SettingsCard from '../../SessionSettingsShared/components/SettingsCard';
import { LibKind, LibOption } from './helpers';

type Filter = 'all' | 'index_fund' | 'equity';

interface Props {
  options: LibOption[];
  selected: Set<string>;
  waiting?: boolean;
  onAdd: (option: LibOption) => void;
  onRemove: (ric: string) => void;
}

const KIND_LABEL: Record<LibKind, string> = {
  index_fund: 'Fund',
  equity: 'Equity',
};

/**
 * The eligible-options library: search + Funds/Equities filter pills over a
 * single scrollable list. Clicking a row toggles it into / out of the basket —
 * replacing the classic dropdown + separate reference-table cross-reference
 * with the same pick-from-library flow the Instrument Universe uses.
 */
const BenchmarkLibraryV2: React.FC<Props> = ({
  options,
  selected,
  waiting,
  onAdd,
  onRemove,
}) => {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  // Asset class / geography live on index funds only; filtering by either naturally narrows the list
  // to funds (equities lack these fields), which is expected.
  const [assetClass, setAssetClass] = useState<string | undefined>();
  const [geography, setGeography] = useState<string | undefined>();

  // Decommissioned funds are only present in `options` because this session already holds them; they
  // are never offerable, so they're excluded from the add-list counts and rows (still rendered on
  // the basket via CompositionBuilderV2's optionIndex, which keeps the full set).
  const counts = useMemo(() => {
    const offerable = options.filter(o => !o.decommissioned);
    return {
      all: offerable.length,
      index_fund: offerable.filter(o => o.kind === 'index_fund').length,
      equity: offerable.filter(o => o.kind === 'equity').length,
    };
  }, [options]);

  const assetClassOptions = useMemo(
    () =>
      Array.from(
        new Set(
          options
            .filter(o => !o.decommissioned && o.assetClass)
            .map(o => o.assetClass as string),
        ),
      ).sort(),
    [options],
  );
  const geographyOptions = useMemo(
    () =>
      Array.from(
        new Set(
          options
            .filter(o => !o.decommissioned && o.geography)
            .map(o => o.geography as string),
        ),
      ).sort(),
    [options],
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter(o => {
      if (o.decommissioned) return false;
      if (filter !== 'all' && o.kind !== filter) return false;
      if (assetClass && o.assetClass !== assetClass) return false;
      if (geography && o.geography !== geography) return false;
      if (!q) return true;
      return (
        o.ric.toLowerCase().includes(q) ||
        o.name.toLowerCase().includes(q) ||
        o.issuer.toLowerCase().includes(q) ||
        (o.assetClass?.toLowerCase().includes(q) ?? false) ||
        (o.geography?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [options, query, filter, assetClass, geography]);

  const pill = (key: Filter, label: string) => (
    <button
      type="button"
      className={`bmv2-fpill${filter === key ? ' is-active' : ''}`}
      onClick={() => setFilter(key)}
    >
      {label}
      <span className="bmv2-fpill__n">{counts[key]}</span>
    </button>
  );

  return (
    <SettingsCard
      title="Options library"
      className="bmv2-card bmv2-flexbody"
      extra={<span className="bmv2-pill-stat">{selected.size} in basket</span>}
    >
      <div className="bmv2-lib-tools">
        <Input
          allowClear
          size="small"
          value={query}
          onChange={e => setQuery(e.target.value)}
          prefix={<SearchOutlined style={{ color: 'var(--s2-muted)' }} />}
          placeholder="Search by name, RIC or issuer"
        />
        <div className="bmv2-lib-filters">
          {pill('all', 'All')}
          {pill('index_fund', 'Funds')}
          {pill('equity', 'Equities')}
        </div>
        <div className="bmv2-lib-dropdowns" style={{ display: 'flex', gap: 8 }}>
          <Select
            allowClear
            showSearch
            size="small"
            style={{ flex: 1, minWidth: 0 }}
            placeholder="Asset class"
            value={assetClass}
            onChange={setAssetClass}
            optionFilterProp="label"
            options={assetClassOptions.map(v => ({ label: v, value: v }))}
          />
          <Select
            allowClear
            showSearch
            size="small"
            style={{ flex: 1, minWidth: 0 }}
            placeholder="Geography"
            value={geography}
            onChange={setGeography}
            optionFilterProp="label"
            options={geographyOptions.map(v => ({ label: v, value: v }))}
          />
        </div>
      </div>

      <div className="bmv2-lib-list">
        {waiting ? (
          <div className="bmv2-lib-empty">
            <Spin size="small" /> &nbsp;Loading eligible options…
          </div>
        ) : visible.length === 0 ? (
          <div className="bmv2-lib-empty">
            {options.length === 0
              ? 'No eligible benchmark options for this session yet.'
              : 'No options match your search.'}
          </div>
        ) : (
          visible.map(option => {
            const added = selected.has(option.ric);
            // A coverage-gated option can't be ADDED, but one already in the basket (e.g. seeded
            // from a saved benchmark that later lost coverage) stays removable so it can be dropped.
            const blocked = !!option.disabled && !added;
            const rowKey = `${option.kind}:${option.ric}`;
            const row = (
              <div
                key={rowKey}
                className={`bmv2-lib-row${added ? ' is-added' : ''}${
                  blocked ? ' is-disabled' : ''
                }`}
                onClick={() => {
                  if (added) {
                    onRemove(option.ric);
                  } else if (!option.disabled) {
                    onAdd(option);
                  }
                }}
              >
                <div className="bmv2-lib-main">
                  <span className="bmv2-lib-title">{option.name}</span>
                  <span className="bmv2-lib-sub">
                    <span className="bmv2-ric">{option.ric}</span>
                    <span
                      className={`bmv2-tag bmv2-tag--${
                        option.kind === 'equity' ? 'equity' : 'fund'
                      }`}
                    >
                      {KIND_LABEL[option.kind]}
                    </span>
                    {option.assetClass ? (
                      <span className="bmv2-tag">{option.assetClass}</span>
                    ) : null}
                    {option.geography ? (
                      <span className="bmv2-tag">{option.geography}</span>
                    ) : null}
                    {option.issuer ? (
                      <span className="bmv2-lib-issuer">{option.issuer}</span>
                    ) : null}
                  </span>
                </div>
                <span
                  className={`bmv2-add${added ? ' is-added' : ''}`}
                  aria-label={
                    added ? 'In basket' : blocked ? 'Unavailable' : 'Add to basket'
                  }
                >
                  {added ? <CheckOutlined /> : <PlusOutlined />}
                </span>
              </div>
            );
            return blocked ? (
              <Tooltip key={rowKey} title={option.disabledReason} placement="left">
                {row}
              </Tooltip>
            ) : (
              row
            );
          })
        )}
      </div>
    </SettingsCard>
  );
};

export default BenchmarkLibraryV2;
